// stores/theme.ts
// Per-device theme preference for CoraFlow. Persists to localStorage.
// Default is "light" for new users. Users who previously selected a theme
// keep their stored choice (read from localStorage under `coraflow-theme`).

import { defineStore } from 'pinia';
import { ref, watch } from 'vue';

export type ThemeMode = 'dark' | 'light';

const STORAGE_KEY = 'coraflow-theme';

function readInitial(): ThemeMode {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'dark' || v === 'light') return v;
  } catch (e) {}
  return 'light';
}

export const useThemeStore = defineStore('theme', () => {
  const mode = ref<ThemeMode>(readInitial());

  // Apply to <html> as a `data-theme` attribute. Tailwind tokens read from
  // CSS variables scoped under [data-theme="..."].
  const apply = () => {
    const el = document.documentElement;
    el.setAttribute('data-theme', mode.value);
    el.style.colorScheme = mode.value;
  };

  // Apply immediately + on every change.
  apply();
  watch(mode, () => {
    apply();
    try {
      localStorage.setItem(STORAGE_KEY, mode.value);
    } catch (e) {}
  });

  function setMode(next: ThemeMode) {
    mode.value = next;
  }
  function toggle() {
    mode.value = mode.value === 'dark' ? 'light' : 'dark';
  }

  return { mode, setMode, toggle };
});
